import Hero from '../components/Hero';
import { useNavigate } from 'react-router-dom';

const Certifications = () => {
  const navigate = useNavigate();

  const programs = [
    {
      tier: '✦ Tier 1: Gold Standard',
      code: 'CEIC',
      title: 'Certified Emotional Intelligence Coach',
      duration: '5 weeks',
      format: 'Online',
      level: 'PAC Level',
      description: 'A comprehensive certification in EQ-based coaching frameworks grounded in the Mayer-Salovey-Caruso model, Goleman’s research, and two decades of workplace studies. Graduate with complete confidence in the evidence behind every technique you use.'
    },
    {
      tier: '✦ Tier 1: Gold Standard',
      code: 'CMPC',
      title: 'Certified Mindfulness & Presence Coach',
      duration: '6 weeks',
      format: 'Online',
      level: 'AAC Level',
      description: 'Grounded in over 1,000 peer-reviewed studies including Kabat-Zinn’s MBSR protocol and Harvard neuroimaging research. Learn to guide clients into presence and clarity, and to cite the science behind every technique you use.'
    },
    {
      tier: '◈ Tier 2: Emerging Evidence',
      code: 'CNLPC',
      title: 'Certified NLP Consciousness Coach',
      duration: '8 weeks',
      format: 'Online + Live',
      level: 'PAC Level',
      description: 'Integrates NLP methodology with consciousness-based coaching principles. Teaches not just the techniques but the evidence context, where NLP overlaps with validated frameworks, where the research is emerging, and how to represent this honestly to clients.'
    },
    {
      tier: '✦✦✦ All Tiers Integrated',
      code: 'CCC-M',
      title: 'Certified Consciousness Coach: Master Level',
      duration: '12 weeks',
      format: 'Online + Intensive',
      level: 'MAC Level',
      description: 'ICCAC’s flagship advanced certification. Bridges neuroscience, transpersonal psychology, emotional intelligence, NLP, and Vedantic wisdom into a unified framework. Explicit about which elements are clinically validated, which are emerging, and which are philosophically grounded.',
      flagship: true
    }
  ];

  return (
    <div>
      <Hero
        title="World-Class Certifications"
        subtitle="Honest About the Evidence"
      />

      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              Every ICCAC certification program is grounded in current research at the appropriate evidence tier and includes 
              Evidence Literacy training so you graduate knowing exactly what you can claim, and why.
            </p>
          </div>

          <div className="space-y-8">
            {programs.map((program, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-lg shadow-lg p-8 ${
                  program.flagship ? 'border-4 border-[#d4af37]' : 'border border-gray-200'
                }`}
              >
                {program.flagship && (
                  <div className="inline-block bg-[#d4af37] text-white px-4 py-1 rounded-full text-sm font-bold mb-4">
                    FLAGSHIP PROGRAM
                  </div>
                )}
                <div className="text-sm text-gray-600 mb-2">{program.tier}</div>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                  <div>
                    <div className="flex items-center mb-2">
                      <span className="text-2xl font-bold text-[#1e3a5f] bg-blue-100 px-4 py-2 rounded mr-4">
                        {program.code}
                      </span>
                      <h3 className="text-2xl font-bold text-gray-800">{program.title}</h3>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-4 md:mt-0">
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                      {program.duration}
                    </span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                      {program.format}
                    </span>
                    <span className="px-3 py-1 bg-blue-100 text-[#1e3a5f] rounded-full text-sm font-medium">
                      {program.level}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-lg leading-relaxed">{program.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Evidence Literacy */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-[#1e3a5f] mb-6">Evidence Literacy</h2>
            <h3 className="text-2xl font-semibold text-gray-800 mb-8">
              You Will Graduate Knowing Exactly What You Can Claim and Why
            </h3>
            <div className="text-lg text-gray-700 space-y-6">
              <p>
                Every ICCAC certification program includes a standalone Evidence Literacy Module. This teaches you to read 
                and evaluate research in your domain, understand the difference between correlation and causation, use claim 
                language appropriate to your evidence tier, and disclose this honestly to clients.
              </p>
              <p>
                Clients increasingly ask about the evidence behind coaching approaches. Coaches who can answer this question 
                with confidence and honesty build deeper trust than those who cannot. ICCAC graduates are trained to be the 
                most credible practitioner in any room they walk into.
              </p>
            </div>
            <div className="mt-12">
              <button
                onClick={() => navigate('/contact')}
                className="bg-[#1e3a5f] text-white px-8 py-3 rounded-md font-semibold hover:bg-[#2d5380] transition-colors text-lg"
              >
                Apply for a Certification →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Certifications;